import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { LinesEditorComponent } from '../../../../../components';
import { ColDef, IRowNode } from '@ag-grid-community/core';
import { FieldTypes } from '@digiwall/ag-grid';
import { CoreModule, LocalizationService } from '@abp/ng.core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateConstants } from './constants';
import { NgSelectComponent } from '@ng-select/ng-select';
import { InvoicingGroup } from '@digiwall/goliath-products/proxy';
import { GoliathContactsEnumerationTypeStore } from '@digiwall/goliath-contacts';
import { CustomerDeliveryNoteToInvoiceDto } from '@digiwall/goliath-documents/proxy';

@Component({
  selector: 'document-delivery-note-to-invoice-grid',
  templateUrl: './delivery-note-to-invoice-grid.component.html',
  styleUrl: './delivery-note-to-invoice-grid.component.scss',
  imports: [LinesEditorComponent, CoreModule, NgSelectComponent],
})
export class DeliveryNoteToInvoiceGridComponent implements AfterViewInit, OnInit {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  @Input() currentDeliveryNoteId: string;
  @Input() set lines(value: CustomerDeliveryNoteToInvoiceDto[]) {
    this._lines = value;

    this._lines
      ?.filter(x => x.id == this.currentDeliveryNoteId)
      ?.forEach(line => ((line as any).isEditable = false));
  }

  @Output() selectionChange = new EventEmitter();

  enumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);

  _lines: CustomerDeliveryNoteToInvoiceDto[];

  public form: FormGroup;

  public get lines() {
    return this._lines;
  }

  columnDefs: ColDef[] = [
    {
      field: 'displayNumber',
      headerName: this.localizationService.instant('::Number'),
      type: FieldTypes.String,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
    },
    {
      field: 'documentDate',
      headerName: this.localizationService.instant('Doc::CustomerQuote:DocumentDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'salesPurchaseDisplayNumber',
      headerName: this.localizationService.instant('Doc::DocRole:SalesPurchaseOrder'),
      type: FieldTypes.String,
    },
    {
      field: 'customerProjectDisplayNumber',
      headerName: this.localizationService.instant('Doc::CustomerProject'),
      type: FieldTypes.String,
    },
    {
      field: 'totalAmount',
      headerName: this.localizationService.instant('Doc::TotalHTVA'),
      type: FieldTypes.Currency,
    },
    {
      field: 'salesPerson',
      headerName: this.localizationService.instant('::Responsible'),
      type: FieldTypes.String,
    },
  ];

  constructor(
    private localizationService: LocalizationService,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  protected initForm(): void {
    this.form = this.formBuilder.group({
      invoicingGroup: [InvoicingGroup.Delivery],
    });
  }

  ngAfterViewInit(): void {
    this.lineGrid.grid.grid.api.setGridOption('onFirstDataRendered', event => {
      const nodesToSelect: IRowNode[] = [];
      event.api.forEachNode(node => {
        if (node.data.id == this.currentDeliveryNoteId) {
          nodesToSelect.push(node);
        }
      });
      event.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
    });

    this.lineGrid.grid.grid.api.setGridOption('onRowSelected', event => {
      if (!event.node.isSelected() && event.data.id == this.currentDeliveryNoteId) {
        event.node.setSelected(true);
      }
    });

    this.lineGrid.grid.grid.api.setGridOption('onSelectionChanged', event => {
      this.selectionChange.emit(event.api.getSelectedNodes().map(node => node.data));
    });
  }

  filterDeliveryNotes(invoicingGroupSelected) {
    const currentLine = this._lines.find(x => x.id == this.currentDeliveryNoteId);
    let linesSelected: any[] = [];
    switch (invoicingGroupSelected?.uniqueCode) {
      case InvoicingGroup.Delivery:
        {
          linesSelected.push(currentLine);
        }
        break;
      case InvoicingGroup.Order:
        {
          linesSelected = this._lines.filter(
            line => line.salesPurchaseDisplayNumber == currentLine.salesPurchaseDisplayNumber,
          );
        }
        break;
      case InvoicingGroup.Day:
        {
          const date = new Date(currentLine.documentDate);
          const dayOfYear = this.getNumberOfDayOfYear(date);

          this._lines.forEach(line => {
            const lineDate = new Date(line.documentDate);
            const dayOfYearOfLine = this.getNumberOfDayOfYear(lineDate);

            if (dayOfYearOfLine == dayOfYear) {
              linesSelected.push(this._lines.find(x => x.id == line.id));
            }
          });
        }
        break;
      case InvoicingGroup.Month:
        {
          const month = new Date(currentLine.documentDate).getMonth();

          this._lines.forEach(line => {
            const lineMonth = new Date(line.documentDate).getMonth();
            if (lineMonth == month) {
              linesSelected.push(this._lines.find(x => x.id == line.id));
            }
          });
        }
        break;
      case InvoicingGroup.Week:
        {
          const numberOfWeek = this.getNumberOfWeek(currentLine.documentDate);

          this._lines.forEach(line => {
            const numberOfWeekLine = this.getNumberOfWeek(line.documentDate);
            if (numberOfWeekLine == numberOfWeek) {
              linesSelected.push(this._lines.find(x => x.id == line.id));
            }
          });
        }
        break;
      case InvoicingGroup.Folder:
        {
          this._lines.forEach(line => {
            if (line.customerProjectId == currentLine.customerProjectId) {
              linesSelected.push(this._lines.find(x => x.id == line.id));
            }
          });
        }
        break;
      default:
        this.form.get('invoicingGroup').setValue(InvoicingGroup.Delivery);
        linesSelected.push(currentLine);
    }

    this.selectLinesInGrid(linesSelected);
  }

  private getNumberOfDayOfYear(date: Date) {
    return (
      (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(date.getFullYear(), DateConstants.JANUARY, DateConstants.DAY_BEFORE_FIRST)) /
      DateConstants.HOURS_PER_DAY /
      DateConstants.SECONDS_PER_MINUTE /
      DateConstants.MINUTES_PER_HOUR /
      DateConstants.MILLISECONDS_PER_SECOND
    );
  }

  private selectLinesInGrid(linesSelected: any[]) {
    if (linesSelected?.length && this.lineGrid?.grid?.grid.api) {
      this.lineGrid.grid.grid.api.deselectAll();

      for (const line of linesSelected) {
        const rowNode = this.lineGrid.grid.grid.api.getRowNode(line.id);
        if (rowNode) {
          rowNode.setSelected(true);
        }
      }
    }
  }

  private getNumberOfWeek(inputDate: string) {
    const date = new Date(inputDate);
    date.setUTCDate(
      date.getUTCDate() +
        DateConstants.THURSDAY_OFFSET -
        (date.getUTCDay() || DateConstants.DAYS_IN_WEEK),
    );
    const yearStart = new Date(
      Date.UTC(date.getUTCFullYear(), DateConstants.JANUARY, DateConstants.FIRST_DAY_OF_MONTH),
    );
    const diffInMs: number = date.getTime() - yearStart.getTime();
    return Math.ceil(
      (diffInMs / DateConstants.MILLISECONDS_PER_DAY + 1) / DateConstants.DAYS_IN_WEEK,
    );
  }
}
